import create from 'zustand';
import { consultSpecialProduct } from 'clients/WebServiceClient';

const MESSAGE_TYPES = {
  USER: 'user',
  CONSULTANT: 'consultant',
};

export const useConsultPopupStore = create((set, get) => ({
  // State
  conversation: [],
  isDone: false,
  canChangeQty: false,
  message: '',
  isLoading: false,

  // Actions
  setMessage: (message) => set({ message }),

  resetState: () =>
    set({
      conversation: [],
      isDone: false,
      canChangeQty: false,
      message: '',
      isLoading: false,
    }),

  respondErrorConversation: () => {
    const { conversation } = get();
    const errorResponse = {
      id: conversation.length + 1,
      type: MESSAGE_TYPES.CONSULTANT,
      content: 'Xin lỗi, có lỗi xảy ra. Vui lòng thử lại sau.',
      timestamp: new Date(),
    };
    set((state) => ({
      conversation: [...state.conversation, errorResponse],
    }));
  },

  addUserMessage: (content) => {
    const { conversation } = get();
    const newMessage = {
      id: conversation.length + 1,
      type: MESSAGE_TYPES.USER,
      content,
      timestamp: new Date(),
    };
    set((state) => ({
      conversation: [...state.conversation, newMessage],
      message: '',
    }));
  },

  addConsultantMessages: async (replies) => {
    const { conversation } = get();

    // Add each reply with a random delay
    for (let i = 0; i < replies.length; i++) {
      const message = {
        id: conversation.length + i + 1,
        type: MESSAGE_TYPES.CONSULTANT,
        content: replies[i],
        timestamp: new Date(),
      };

      // Delay 100ms to 500ms, to simulate real conversation
      await new Promise((resolve) => setTimeout(resolve, (() => Math.random() * 400 + 100)()));

      set((state) => ({
        conversation: [...state.conversation, message],
      }));
    }
  },

  handleInitialConsultation: async (product) => {
    set({ isLoading: true });

    const initialMessage = {
      id: 1,
      type: MESSAGE_TYPES.USER,
      content: `Tôi cần tư vấn thêm/thay đổi số lượng sản phẩm ${product?.name ? `"<b>${product?.name}</b>"` : 'này'}.`,
      timestamp: new Date(),
    };

    set({
      conversation: [initialMessage],
      isDone: false,
      canChangeQty: false,
    });

    try {
      const res = await consultSpecialProduct({ slug: product.slug });

      if (res.status === 'OK' || (res.status === 'INVALID' && res.data && res.data.length > 0)) {
        const replies = res.data[0].replies || [];
        const canChangeQty = res.data[0].canChangeQty || false;
        const isDone = res.data[0].isDone || false;

        await get().addConsultantMessages(replies);

        // Update canChangeQty and isDone from response
        set({ canChangeQty, isDone });
      } else {
        get().respondErrorConversation();
      }
    } catch (error) {
      console.error('Error in initial consultation:', error);
      get().respondErrorConversation();
    } finally {
      set({ isLoading: false });
    }
  },

  handleSendMessage: async (product) => {
    const { message } = get();

    if (!message.trim()) return;

    set({ isLoading: true });
    get().addUserMessage(message);

    try {
      const res = await consultSpecialProduct({
        slug: product.slug,
        message: message,
      });

      if (res.status === 'OK' || (res.status === 'INVALID' && res.data && res.data.length > 0)) {
        const replies = res.data[0].replies || [];
        const canChangeQty = res.data[0].canChangeQty || false;
        const isDone = res.data[0].isDone || false;

        await get().addConsultantMessages(replies);

        // Update canChangeQty and isDone from response
        set({ canChangeQty, isDone });

        // TODO: Track event
        // See: src/context/Cart/CartContext.js updateCartItem
        //   gtag.addToCart({ ...payload.product, quantity: dataUpdate?.q || 0, sellerInfo });
        //   gtag.addToCartInPage({ ...payload.product, quantity: dataUpdate?.q || 0, sellerInfo }, router.pathname);
      } else {
        get().respondErrorConversation();
      }
    } catch (error) {
      console.error('Error consulting special product:', error);
      get().respondErrorConversation();
    } finally {
      set({ isLoading: false });
    }
  },

  handleChangeQuantity: async (product) => {
    set({ isLoading: true });

    const changeQuantityMessage = {
      id: get().conversation.length + 1,
      type: MESSAGE_TYPES.USER,
      content: `Tôi cần thay đổi số lượng sản phẩm ${product?.name ? `"<b>${product?.name}</b>"` : 'này'}.`,
      timestamp: new Date(),
    };

    set((state) => ({
      conversation: [...state.conversation, changeQuantityMessage],
      isDone: false,
      canChangeQty: false,
    }));

    try {
      const res = await consultSpecialProduct({
        slug: product.slug,
      });

      if (res.status === 'OK' || (res.status === 'INVALID' && res.data && res.data.length > 0)) {
        const replies = res.data[0].replies || [];
        const canChangeQty = res.data[0].canChangeQty || false;
        const isDone = res.data[0].isDone || false;

        await get().addConsultantMessages(replies);

        // Update canChangeQty and isDone from response
        set({ canChangeQty, isDone });
      } else {
        get().respondErrorConversation();
      }
    } catch (error) {
      console.error('Error changing quantity:', error);
      get().respondErrorConversation();
    } finally {
      set({ isLoading: false });
    }
  },
}));
